import { useEffect, CompositionEvent, useState } from 'react';
import { debounce } from 'lodash';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  InputAdornment,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import {
  StyledContainer,
  StyledTextField,
} from './InternetResourceFilter.style';
import { useSelector } from 'react-redux';
import { getterInternetResourceFilter } from '~/store/internetResource/internetResourceGetter';
import {
  defaultOption,
  getInternetHeader,
  internetFuzzySearchFieldsOption,
  fuzzySearchFieldsText,
} from '~/utils/constants';
import { useAppDispatch } from '~/store';
import { setSearchCondition } from '~/store/internetResource';
import {
  TUniqInternetResourceProps,
  internetCategory,
  resourceEduCode,
} from '~/api/oneBoxV3/oneBoxV3.type';
import { TInternetSearchCondition } from '~/store/internetResource/internetResource.type';
import { THandleSelectResourceOption } from '~/components/Admin/AdminComponent.type';
import { useLazyInternetDropDownListQuery } from '~/api/oneBoxV3';

const renderInput = (
  params: AutocompleteRenderInputParams,
  id: keyof TInternetSearchCondition,
) => <StyledTextField color="info" {...params} label={getInternetHeader(id)} />;

const getOptionLabel = (option: string) => (option === 'all' ? '全部' : option);

export const InternetResourceFilter = () => {
  const [dropDownList, setDropDownList] =
    useState<TUniqInternetResourceProps>(defaultOption);
  const {
    category,
    years,
    eduCodes,
    subjects,
    grades,
    fuzzySearch,
    fuzzySearchFields,
    isLaunch,
  } = useSelector(getterInternetResourceFilter);
  const [fuzzySearchValue, setFuzzySearchValue] = useState(fuzzySearch || '');
  const [fuzzySearchKeyDownCode, setFuzzySearchKeyDownCode] = useState('');
  const [isComposing, setIsComposing] = useState(false);

  const [reDropDownList] = useLazyInternetDropDownListQuery();
  const dispatch = useAppDispatch();
  const categoryOptions = ['學校', '公開'];

  const gradeOptions = Array.from(
    { length: eduCodes === resourceEduCode.E ? 6 : 3 },
    (_, i) => (i + 1).toString(),
  );

  const handleSelectOption = ({ event, id }: THandleSelectResourceOption) => {
    const idx = Number((event.target as HTMLButtonElement).dataset.optionIndex);
    dispatch(
      setSearchCondition({
        [`${id}`]:
          idx === 0 ? 'all' : (event.target as HTMLButtonElement).innerText,
        ...(id === 'eduCodes' && { grades: 'all' }),
      }),
    );
  };

  useEffect(() => {
    const getDropDownList = () => {
      void (async () => {
        try {
          const dropDownData = await reDropDownList({
            isLaunch,
            categories:
              category === 'all'
                ? [internetCategory.SCHOOL, internetCategory.PUBLIC]
                : [category],
            internetResourceProps: [
              'years',
              'eduCodes',
              'subjects',
              'grades',
              'resources1',
              'resources2',
            ],
          }).unwrap();
          if (!dropDownData.err)
            setDropDownList(dropDownData.data?.uniqInternetResourceProps ?? {});
        } catch (error) {
          console.log('dropDownListApi error', error);
        }
      })();
    };
    getDropDownList();
  }, [category, isLaunch]);

  useEffect(() => {
    const debouncedUpdate = debounce((payload: string) => {
      dispatch(setSearchCondition({ fuzzySearch: payload }));
    }, 300);
    if (!isComposing && fuzzySearchKeyDownCode === 'Enter') {
      debouncedUpdate(fuzzySearchValue);
    }

    return () => debouncedUpdate.cancel();
  }, [fuzzySearchValue, fuzzySearchKeyDownCode]);

  // 偵測是否正在輸入中文
  const handleComposition = (e: CompositionEvent<HTMLInputElement>) => {
    if (e.type === 'compositionstart') {
      setIsComposing(true);
    } else if (e.type === 'compositionend') {
      setIsComposing(false);
      setFuzzySearchKeyDownCode('');
    }
  };

  return (
    <StyledContainer>
      <Autocomplete
        disablePortal
        options={['all', ...categoryOptions]}
        size="small"
        getOptionLabel={getOptionLabel}
        renderInput={params => renderInput(params, 'category')}
        value={category}
        onChange={event => {
          handleSelectOption({
            event,
            id: 'category',
            customOptions: categoryOptions,
          });
        }}
      />
      <Autocomplete
        disablePortal
        options={['all', ...(dropDownList.years ?? [])]}
        size="small"
        getOptionLabel={getOptionLabel}
        renderInput={params => renderInput(params, 'years')}
        value={years}
        onChange={event =>
          handleSelectOption({
            event,
            id: 'years',
            customOptions: dropDownList.years ?? [],
          })
        }
      />
      <Autocomplete
        disablePortal
        options={['all', ...(dropDownList.eduCodes ?? [])]}
        size="small"
        getOptionLabel={getOptionLabel}
        renderInput={params => renderInput(params, 'eduCodes')}
        value={eduCodes}
        onChange={event =>
          handleSelectOption({
            event,
            id: 'eduCodes',
            customOptions: dropDownList.eduCodes ?? [],
          })
        }
      />
      <Autocomplete
        disablePortal
        options={['all', ...(dropDownList.subjects ?? [])]}
        size="small"
        getOptionLabel={getOptionLabel}
        renderInput={params => renderInput(params, 'subjects')}
        value={subjects}
        onChange={event =>
          handleSelectOption({
            event,
            id: 'subjects',
            customOptions: dropDownList.subjects ?? [],
          })
        }
      />
      <Autocomplete
        disablePortal
        options={['all', ...(dropDownList.grades ?? [])]}
        size="small"
        getOptionLabel={getOptionLabel}
        renderInput={params => renderInput(params, 'grades')}
        value={grades}
        onChange={event =>
          handleSelectOption({
            event,
            id: 'grades',
            customOptions: gradeOptions,
          })
        }
      />
      <Autocomplete
        disablePortal
        options={internetFuzzySearchFieldsOption}
        size="small"
        getOptionLabel={option => fuzzySearchFieldsText[option]}
        renderInput={params => renderInput(params, 'fuzzySearchFields')}
        value={fuzzySearchFields ? fuzzySearchFields[0] : undefined}
        onChange={(event, value) => {
          if (!value) return;
          dispatch(
            setSearchCondition({
              fuzzySearchFields: [value],
            }),
          );
        }}
      />
      <StyledTextField
        placeholder={getInternetHeader('fuzzySearch')}
        fullWidth
        sx={{ gridColumnStart: '2', gridColumnEnd: '6' }}
        size="small"
        value={fuzzySearchValue}
        onChange={event => setFuzzySearchValue(event.target.value)}
        onKeyDown={event => setFuzzySearchKeyDownCode(event.key)}
        onCompositionUpdate={handleComposition}
        onCompositionEnd={handleComposition}
        onCompositionStart={handleComposition}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />
    </StyledContainer>
  );
};
